import { useContext, useEffect, useMemo, useState } from 'react'
import CachedIcon from '@mui/icons-material/Cached'
import RefContext from '@/contexts/RefContext'
import { useMenuCategories } from '@/pages/menu/hooks/useMenuCategories'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import { useOrderAgain } from '@/pages/menu/hooks/useOrderAgain'
import type { MenuItem as MenuItemData } from '@/shared/api/menu'
import type { PastOrderItem } from '@/shared/api/orderAgain'
import { getOrderAgain } from '@/shared/api/orderAgain'
import MenuItem from '@/components/menu/MenuItem'
import EmptySearchIllustration from '@/components/menu/EmptySearchIllustration'
import MenuSkeleton from '@/components/menu/MenuSkeleton'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { useAppSelector } from '@/store'
import { selectDineInTableName } from '@/store/ticketSlice'
import {
	CategoryHeadingRow,
	CategoryName,
	CategoryResultCount,
	CategorySection,
	ClearSearchButton,
	EmptySearchHint,
	EmptySearchState,
	EmptySearchTitle,
	ItemGrid,
	ItemSlot,
	LiveRegion,
	MenuPageRoot,
	OrderAgainHeadingRow,
	OrderAgainIcon,
	PaginationButton,
	PaginationIcon,
	PaginationLabel,
	SearchResultsHeader,
	SearchResultsTitle,
} from './MenuPage.styled'

const ITEMS_PER_PAGE = 2

function toMenuItemData(item: PastOrderItem): MenuItemData {
	return {
		ref: item.ref,
		name: item.name,
		description: item.lastOrderModifiers,
		price: item.totalPrice,
		imageUrl800x800: item.imageUrl800x800,
		stockStatus: item.stockStatus,
	}
}

interface MenuPageProps {
	scheduleId?: string
	searchTerm?: string
	onClearSearch?: () => void
	currency?: string
}

export default function MenuPage({
	scheduleId = '',
	searchTerm = '',
	onClearSearch = () => {},
	currency = 'USD',
}: MenuPageProps) {
	const locationRef = useContext(RefContext) ?? ''
	const { t } = useFormatMessage()
	const dineInTableName = useAppSelector(selectDineInTableName)
	const isDineIn = !!dineInTableName

	const { data: categoriesData } = useMenuCategories(locationRef, scheduleId)
	const categories = useMemo(() => categoriesData?.categories ?? [], [categoriesData?.categories])

	const { data: itemsData, isLoading: itemsLoading } = useMenuItems(locationRef, scheduleId)
	const isLoading = itemsLoading

	const { data: orderAgainData } = useOrderAgain(locationRef, scheduleId, isDineIn)

	// Pagination state is keyed by query identity so it auto-resets when the schedule
	// or order type changes — no useEffect needed to sync with orderAgainData.
	const paginationKey = `${locationRef}|${scheduleId}|${String(isDineIn)}`
	const [pagination, setPagination] = useState<{
		forKey: string
		extraItems: PastOrderItem[]
		itemsToShow: number
		serverPage: number
	}>({ forKey: paginationKey, extraItems: [], itemsToShow: ITEMS_PER_PAGE, serverPage: 1 })
	const [isFetchingMore, setIsFetchingMore] = useState(false)

	const isCurrent = pagination.forKey === paginationKey
	const page1Items = orderAgainData?.pastOrderItems ?? []
	const allItems = isCurrent ? [...page1Items, ...pagination.extraItems] : page1Items
	const visibleCount = isCurrent ? pagination.itemsToShow : ITEMS_PER_PAGE
	const serverPage = isCurrent ? pagination.serverPage : 1
	const visibleItems = allItems.slice(0, visibleCount)
	const hasMoreLocal = visibleCount < allItems.length
	// STO-3806: compare fetched page against totalPages rather than allItems.length against
	// totalItems. totalItems includes inactive POI items the API omits from pastOrderItems,
	// so allItems.length never reaches totalItems when inactive items exist — canShowMore
	// stays true permanently and Show Less never appears.
	const hasMoreServer = serverPage < (orderAgainData?.totalPages ?? 1)
	const canShowMore = hasMoreLocal || hasMoreServer

	async function handleShowMore() {
		if (isFetchingMore) return
		if (hasMoreLocal) {
			setPagination((prev) =>
				prev.forKey === paginationKey
					? { ...prev, itemsToShow: prev.itemsToShow + ITEMS_PER_PAGE }
					: {
							forKey: paginationKey,
							extraItems: [],
							serverPage: 1,
							itemsToShow: ITEMS_PER_PAGE * 2,
						},
			)
			return
		}
		setIsFetchingMore(true)
		try {
			const nextPage = serverPage + 1
			const more = await getOrderAgain(locationRef, scheduleId, nextPage, isDineIn)
			setPagination((prev) => ({
				forKey: paginationKey,
				extraItems: isCurrent
					? [...prev.extraItems, ...more.pastOrderItems]
					: more.pastOrderItems,
				serverPage: nextPage,
				itemsToShow: prev.itemsToShow + ITEMS_PER_PAGE,
			}))
		} finally {
			setIsFetchingMore(false)
		}
	}

	function handleShowLess() {
		setPagination((prev) => ({ ...prev, forKey: paginationKey, itemsToShow: ITEMS_PER_PAGE }))
		document.getElementById('category-order-again')?.scrollIntoView({ behavior: 'smooth' })
	}

	const itemsByRef = useMemo(() => {
		const map = new Map<string, MenuItemData>()
		for (const item of itemsData?.items ?? []) {
			map.set(item.ref, item)
		}
		return map
	}, [itemsData?.items])

	const filteredCategories = useMemo(() => {
		if (!searchTerm) return categories
		return categories
			.map((category) => ({
				...category,
				items: category.items.filter((itemRef) => {
					const item = itemsByRef.get(itemRef)
					return item && item.name.toLowerCase().includes(searchTerm.toLowerCase())
				}),
			}))
			.filter((category) => category.items.length > 0)
	}, [categories, itemsByRef, searchTerm])

	const isSearching = searchTerm.length > 0

	const orderAgainSearchResults = isSearching
		? allItems.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
		: visibleItems

	const hasResults = filteredCategories.length > 0 || orderAgainSearchResults.length > 0

	const totalResultCount = useMemo(() => {
		const itemCount = filteredCategories.reduce((sum, cat) => sum + cat.items.length, 0)
		return itemCount + orderAgainSearchResults.length
	}, [filteredCategories, orderAgainSearchResults])

	const [searchAnnouncement, setSearchAnnouncement] = useState('')
	useEffect(() => {
		// Defer so the live region content change is picked up by screen readers after
		// results have rendered — immediate updates can be swallowed by some AT.
		// When search is cleared the announcement resets after the same short delay.
		const id = setTimeout(() => {
			if (!isSearching || isLoading) {
				setSearchAnnouncement('')
				return
			}
			setSearchAnnouncement(
				hasResults
					? t('page:menu:searchResults:announcement', {
							count: totalResultCount,
							searchTerm,
						})
					: t('page:menu:searchResults:noResults:announcement', { searchTerm }),
			)
		}, 100)
		return () => clearTimeout(id)
	}, [isSearching, isLoading, hasResults, totalResultCount, searchTerm, t])

	if (isLoading)
		return (
			<>
				<LiveRegion role="status" aria-live="polite" aria-atomic="true">
					{searchAnnouncement}
				</LiveRegion>
				<MenuSkeleton />
			</>
		)

	return (
		<MenuPageRoot>
			<LiveRegion role="status" aria-live="polite" aria-atomic="true">
				{searchAnnouncement}
			</LiveRegion>
			{isSearching && (
				<SearchResultsHeader>
					<SearchResultsTitle>
						{t('page:menu:searchResults:header', { searchTerm })}
					</SearchResultsTitle>
					<ClearSearchButton
						type="button"
						onPointerDown={(e) => e.stopPropagation()}
						onClick={onClearSearch}
					>
						{t('page:menu:clearSearch:button')}
					</ClearSearchButton>
				</SearchResultsHeader>
			)}

			{isSearching && !hasResults && (
				<EmptySearchState>
					<EmptySearchIllustration />
					<EmptySearchTitle>{t('page:menu:searchEmpty:title')}</EmptySearchTitle>
					<EmptySearchHint>{t('page:menu:searchEmpty:hint')}</EmptySearchHint>
				</EmptySearchState>
			)}

			{orderAgainData !== undefined && orderAgainSearchResults.length > 0 && (
				<CategorySection id="category-order-again">
					<OrderAgainHeadingRow>
						<OrderAgainIcon>
							<CachedIcon fontSize="inherit" color="inherit" aria-hidden />
						</OrderAgainIcon>
						<CategoryName>{t('page:menu:orderAgain:name')}</CategoryName>
					</OrderAgainHeadingRow>
					<ItemGrid role="list">
						{orderAgainSearchResults.map((item) => (
							<ItemSlot key={item.pastOrderItemGuid} role="listitem">
								<MenuItem
									item={toMenuItemData(item)}
									isReOrder
									pastOrderItemId={item.pastOrderItemGuid}
									currency={currency}
								/>
							</ItemSlot>
						))}
					</ItemGrid>
					{canShowMore && !isSearching && (
						<PaginationButton
							type="button"
							disabled={isFetchingMore}
							onClick={() => {
								void handleShowMore()
							}}
						>
							<PaginationIcon>+</PaginationIcon>
							<PaginationLabel>{t('page:menu:orderAgain:showMore')}</PaginationLabel>
						</PaginationButton>
					)}
					{!canShowMore && allItems.length > ITEMS_PER_PAGE && !isSearching && (
						<PaginationButton type="button" onClick={handleShowLess}>
							<PaginationIcon>−</PaginationIcon>
							<PaginationLabel>{t('page:menu:orderAgain:showLess')}</PaginationLabel>
						</PaginationButton>
					)}
				</CategorySection>
			)}

			{filteredCategories.map((category) => {
				const resolvedItems = category.items
					.map((itemRef) => itemsByRef.get(itemRef))
					.filter((item): item is MenuItemData => item !== undefined)

				// Sold-out items sorted to the bottom of each category
				const sortedItems = [
					...resolvedItems.filter((i) => i.stockStatus !== 'OUT_OF_STOCK'),
					...resolvedItems.filter((i) => i.stockStatus === 'OUT_OF_STOCK'),
				]

				if (sortedItems.length === 0) return null

				return (
					<CategorySection id={`category-${category.ref}`} key={category.ref}>
						<CategoryHeadingRow>
							<CategoryName>{category.name}</CategoryName>
							{isSearching && (
								<CategoryResultCount>
									{t('page:menu:searchResultsCount', {
										count: sortedItems.length,
									})}
								</CategoryResultCount>
							)}
						</CategoryHeadingRow>

						<ItemGrid role="list">
							{sortedItems.map((item) => (
								<ItemSlot key={item.ref} role="listitem">
									<MenuItem item={item} currency={currency} />
								</ItemSlot>
							))}
						</ItemGrid>
					</CategorySection>
				)
			})}
		</MenuPageRoot>
	)
}
